import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const AVAILABILITY_STYLES = {
  available: { bg: 'rgba(52,211,153,0.12)', text: '#34d399', border: 'rgba(52,211,153,0.3)', label: 'Available' },
  assigned: { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', border: 'rgba(96,165,250,0.3)', label: 'Assigned' },
  maintenance: { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)', label: 'Maintenance' },
  unavailable: { bg: 'rgba(239,68,68,0.12)', text: '#ef4444', border: 'rgba(239,68,68,0.3)', label: 'Unavailable' },
};

// ── Vehicle Selection Modal ────────────────────────────────────────────────────
const VehicleSelectModal = ({ accountId, existingAssignments, onClose, onAssigned }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [allocationLimit, setAllocationLimit] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const existingIds = new Set(existingAssignments?.map(a => a?.vehicle_id));

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const { data, error: fetchErr } = await supabase
          ?.from('vehicles')
          ?.select('id, title, make, model, year, license_plate, vehicle_type, status, location_address')
          ?.in('status', ['approved', 'available'])
          ?.order('make', { ascending: true });
        if (fetchErr) throw fetchErr;
        setVehicles(data || []);
      } catch (err) {
        setError(err?.message || 'Failed to load vehicles');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const filteredVehicles = vehicles?.filter(v => {
    if (existingIds?.has(v?.id)) return false;
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (
      v?.make?.toLowerCase()?.includes(q) ||
      v?.model?.toLowerCase()?.includes(q) ||
      v?.title?.toLowerCase()?.includes(q) ||
      v?.license_plate?.toLowerCase()?.includes(q)
    );
  });

  const handleAssign = async () => {
    if (!selectedVehicleId) return;
    setSaving(true);
    setError(null);
    try {
      const { error: insertErr } = await supabase
        ?.from('corporate_vehicle_assignments')
        ?.insert({
          corporate_account_id: accountId,
          vehicle_id: selectedVehicleId,
          allocation_limit: allocationLimit,
          notes: notes || null,
          availability_status: 'assigned',
          assigned_at: new Date()?.toISOString(),
        });
      if (insertErr) throw insertErr;
      onAssigned();
    } catch (err) {
      setError(err?.message || 'Failed to assign vehicle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-surface rounded-2xl border border-border w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
          <h3 className="text-text-primary font-bold text-base flex items-center gap-2">
            <Icon name="Car" size={18} className="text-[#ffd700]" />
            Assign Fleet Vehicle
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border flex-shrink-0">
          <div className="relative">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e?.target?.value)}
              placeholder="Search by make, model, or plate…"
              className="input-field w-full text-sm pl-9"
            />
          </div>
        </div>

        {/* Vehicle List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
              {error}
            </div>
          )}
          {loading && (
            <div className="space-y-2">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-border animate-pulse" />
              ))}
            </div>
          )}
          {!loading && filteredVehicles?.length === 0 && (
            <div className="text-center py-10">
              <Icon name="Car" size={36} className="text-text-secondary mx-auto mb-2 opacity-30" />
              <p className="text-text-secondary text-sm">
                {searchQuery ? 'No vehicles match your search' : 'No available vehicles to assign'}
              </p>
            </div>
          )}
          {!loading && filteredVehicles?.map(vehicle => (
            <button
              key={vehicle?.id}
              onClick={() => setSelectedVehicleId(vehicle?.id === selectedVehicleId ? null : vehicle?.id)}
              className="w-full text-left rounded-xl p-3 flex items-center gap-3 transition-all"
              style={{
                background: selectedVehicleId === vehicle?.id ? 'rgba(255,215,0,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selectedVehicleId === vehicle?.id ? 'rgba(255,215,0,0.35)' : 'rgba(255,255,255,0.07)'}`,
              }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: selectedVehicleId === vehicle?.id ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)' }}
              >
                <Icon name="Car" size={16} style={{ color: selectedVehicleId === vehicle?.id ? '#ffd700' : 'var(--color-text-secondary)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-text-primary text-sm font-semibold truncate">
                  {vehicle?.year} {vehicle?.make} {vehicle?.model}
                </p>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  {vehicle?.license_plate && (
                    <span className="text-text-secondary text-xs flex items-center gap-1">
                      <Icon name="Hash" size={10} /> {vehicle?.license_plate}
                    </span>
                  )}
                  {vehicle?.vehicle_type && (
                    <span className="text-text-secondary text-xs capitalize">{vehicle?.vehicle_type}</span>
                  )}
                  {vehicle?.location_address && (
                    <span className="text-text-secondary text-xs flex items-center gap-1 truncate">
                      <Icon name="MapPin" size={10} /> {vehicle?.location_address}
                    </span>
                  )}
                </div>
              </div>
              {selectedVehicleId === vehicle?.id && (
                <Icon name="CheckCircle2" size={18} className="flex-shrink-0" style={{ color: '#ffd700' }} />
              )}
            </button>
          ))}
        </div>

        {/* Allocation Config */}
        {selectedVehicleId && (
          <div className="p-4 border-t border-border space-y-3 flex-shrink-0" style={{ background: 'rgba(255,215,0,0.04)' }}>
            <h4 className="text-text-primary text-sm font-semibold flex items-center gap-2">
              <Icon name="Settings2" size={14} className="text-[#ffd700]" />
              Assignment Configuration
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  Allocation Limit <span className="text-text-secondary font-normal">(max concurrent bookings)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={allocationLimit}
                  onChange={e => setAllocationLimit(Math.max(1, parseInt(e?.target?.value) || 1))}
                  className="input-field w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Notes (optional)</label>
                <input
                  value={notes}
                  onChange={e => setNotes(e?.target?.value)}
                  placeholder="e.g. Reserved for executives"
                  className="input-field w-full text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="btn-secondary text-sm px-4 py-2">Cancel</button>
          <button
            onClick={handleAssign}
            disabled={!selectedVehicleId || saving}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <><Icon name="Loader2" size={14} className="animate-spin" /> Assigning…</>
            ) : (
              <><Icon name="Plus" size={14} /> Assign Vehicle</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Assignment Modal ──────────────────────────────────────────────────────
const EditAssignmentModal = ({ assignment, onClose, onSaved }) => {
  const [allocationLimit, setAllocationLimit] = useState(assignment?.allocation_limit || 1);
  const [availabilityStatus, setAvailabilityStatus] = useState(assignment?.availability_status || 'assigned');
  const [notes, setNotes] = useState(assignment?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: updateErr } = await supabase
        ?.from('corporate_vehicle_assignments')
        ?.update({
          allocation_limit: allocationLimit,
          availability_status: availabilityStatus,
          notes: notes || null,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', assignment?.id);
      if (updateErr) throw updateErr;
      onSaved();
    } catch (err) {
      setError(err?.message || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-surface rounded-2xl border border-border w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-text-primary font-bold text-base flex items-center gap-2">
            <Icon name="Edit3" size={18} className="text-[#ffd700]" />
            Edit Vehicle Assignment
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors">
            <Icon name="X" size={18} className="text-text-secondary" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Vehicle Info */}
          <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Icon name="Car" size={16} className="text-text-secondary flex-shrink-0" />
            <div>
              <p className="text-text-primary text-sm font-semibold">
                {assignment?.vehicles?.year} {assignment?.vehicles?.make} {assignment?.vehicles?.model}
              </p>
              {assignment?.vehicles?.license_plate && (
                <p className="text-text-secondary text-xs">{assignment?.vehicles?.license_plate}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Availability Status</label>
            <select
              value={availabilityStatus}
              onChange={e => setAvailabilityStatus(e?.target?.value)}
              className="input-field w-full text-sm"
            >
              {Object.entries(AVAILABILITY_STYLES)?.map(([key, val]) => (
                <option key={key} value={key}>{val?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Allocation Limit <span className="text-text-secondary font-normal">(max concurrent bookings)</span>
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={allocationLimit}
              onChange={e => setAllocationLimit(Math.max(1, parseInt(e?.target?.value) || 1))}
              className="input-field w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Notes</label>
            <input
              value={notes}
              onChange={e => setNotes(e?.target?.value)}
              placeholder="e.g. Reserved for executives"
              className="input-field w-full text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <button onClick={onClose} className="btn-secondary text-sm px-4 py-2">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main VehicleAssignmentPanel ────────────────────────────────────────────────
const VehicleAssignmentPanel = ({ accountId, accountName }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        ?.from('corporate_vehicle_assignments')
        ?.select(`
          *,
          vehicles(id, title, make, model, year, license_plate, vehicle_type, location_address, status)
        `)
        ?.eq('corporate_account_id', accountId)
        ?.order('assigned_at', { ascending: false });
      if (fetchErr) throw fetchErr;
      setAssignments(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load vehicle assignments');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleRemove = async (assignmentId) => {
    setRemoving(assignmentId);
    try {
      const { error: deleteErr } = await supabase
        ?.from('corporate_vehicle_assignments')
        ?.delete()
        ?.eq('id', assignmentId);
      if (deleteErr) throw deleteErr;
      showToast('Vehicle unassigned successfully');
      setRemoveConfirm(null);
      fetchAssignments();
    } catch (err) {
      showToast(err?.message || 'Failed to remove assignment', 'error');
    } finally {
      setRemoving(null);
    }
  };

  const filteredAssignments = assignments?.filter(a =>
    filterStatus === 'all' ? true : a?.availability_status === filterStatus
  );

  const statusCounts = assignments?.reduce((acc, a) => {
    acc[a?.availability_status] = (acc?.[a?.availability_status] || 0) + 1;
    return acc;
  }, {});

  const totalAllocation = assignments?.reduce((sum, a) => sum + (a?.allocation_limit || 0), 0);

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-20 right-5 z-[400] px-4 py-3 rounded-xl text-sm font-medium shadow-xl flex items-center gap-2"
          style={{
            background: toast?.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(52,211,153,0.15)',
            border: `1px solid ${toast?.type === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(52,211,153,0.4)'}`,
            color: toast?.type === 'error' ? '#ef4444' : '#34d399',
          }}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={16} />
          {toast?.msg}
        </div>
      )}

      {/* Sub-header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Icon name="Truck" size={16} className="text-[#a78bfa]" />
          <h4 className="text-text-primary text-sm font-semibold">
            Fleet Vehicle Assignments
            <span className="ml-2 text-text-secondary font-normal">({assignments?.length} vehicle{assignments?.length !== 1 ? 's' : ''})</span>
          </h4>
        </div>
        <button
          onClick={() => setShowAssignModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
          style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}
        >
          <Icon name="Plus" size={13} />
          Assign Vehicle
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'Total Assigned', value: assignments?.length, color: '#a78bfa', icon: 'Car' },
          { label: 'Available', value: statusCounts?.available || 0, color: '#34d399', icon: 'CheckCircle2' },
          { label: 'In Use', value: statusCounts?.assigned || 0, color: '#60a5fa', icon: 'Navigation' },
          { label: 'Total Allocation', value: totalAllocation, color: '#ffd700', icon: 'BarChart2' },
        ]?.map(stat => (
          <div
            key={stat?.label}
            className="rounded-lg p-2.5 flex items-center gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${stat?.color}18` }}>
              <Icon name={stat?.icon} size={13} style={{ color: stat?.color }} />
            </div>
            <div>
              <p className="text-text-primary font-bold text-sm leading-none">{stat?.value}</p>
              <p className="text-text-secondary text-[10px] mt-0.5">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'available', 'assigned', 'maintenance', 'unavailable']?.map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all capitalize"
            style={{
              background: filterStatus === s ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)',
              color: filterStatus === s ? '#a78bfa' : 'var(--color-text-secondary)',
              border: `1px solid ${filterStatus === s ? 'rgba(167,139,250,0.3)' : 'rgba(255,255,255,0.07)'}`,
            }}
          >
            {s === 'all' ? 'All' : AVAILABILITY_STYLES?.[s]?.label || s}
            {s !== 'all' && statusCounts?.[s] ? (
              <span className="ml-1 px-1 py-0.5 rounded-full text-[9px]" style={{ background: 'rgba(255,255,255,0.1)' }}>
                {statusCounts?.[s]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-border animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filteredAssignments?.length === 0 && (
        <div className="text-center py-8 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)' }}>
          <Icon name="Car" size={32} className="text-text-secondary mx-auto mb-2 opacity-30" />
          <p className="text-text-secondary text-sm">
            {filterStatus !== 'all' ? `No ${AVAILABILITY_STYLES?.[filterStatus]?.label?.toLowerCase()} vehicles` : 'No vehicles assigned yet'}
          </p>
          {filterStatus === 'all' && (
            <button
              onClick={() => setShowAssignModal(true)}
              className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)' }}
            >
              + Assign First Vehicle
            </button>
          )}
        </div>
      )}

      {/* Assignment Rows */}
      {!loading && filteredAssignments?.map(assignment => {
        const avail = AVAILABILITY_STYLES?.[assignment?.availability_status] || AVAILABILITY_STYLES?.assigned;
        const vehicle = assignment?.vehicles;

        return (
          <div
            key={assignment?.id}
            className="rounded-xl p-3 flex items-center gap-3 flex-wrap"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {/* Vehicle Icon */}
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${avail?.text}18` }}>
              <Icon name="Car" size={16} style={{ color: avail?.text }} />
            </div>

            {/* Vehicle Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-text-primary text-sm font-semibold">
                  {vehicle?.year} {vehicle?.make} {vehicle?.model}
                </span>
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  style={{ background: avail?.bg, color: avail?.text, border: `1px solid ${avail?.border}` }}
                >
                  {avail?.label}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {vehicle?.license_plate && (
                  <span className="text-text-secondary text-xs flex items-center gap-1">
                    <Icon name="Hash" size={10} /> {vehicle?.license_plate}
                  </span>
                )}
                {vehicle?.vehicle_type && (
                  <span className="text-text-secondary text-xs capitalize">{vehicle?.vehicle_type}</span>
                )}
                <span className="text-xs flex items-center gap-1" style={{ color: '#ffd700' }}>
                  <Icon name="BarChart2" size={10} />
                  Limit: <strong>{assignment?.allocation_limit}</strong>
                </span>
                {assignment?.notes && (
                  <span className="text-text-secondary text-xs flex items-center gap-1 truncate max-w-[160px]">
                    <Icon name="FileText" size={10} /> {assignment?.notes}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setEditAssignment(assignment)}
                className="p-1.5 rounded-lg border border-border hover:bg-surface-hover transition-colors"
                title="Edit assignment"
              >
                <Icon name="Edit3" size={13} className="text-text-secondary" />
              </button>
              <button
                onClick={() => setRemoveConfirm(assignment)}
                className="p-1.5 rounded-lg border transition-colors"
                style={{ borderColor: 'rgba(239,68,68,0.3)', color: '#ef4444' }}
                title="Remove assignment"
              >
                <Icon name="Unlink" size={13} />
              </button>
            </div>
          </div>
        );
      })}

      {/* Assign Modal */}
      {showAssignModal && (
        <VehicleSelectModal
          accountId={accountId}
          existingAssignments={assignments}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => {
            setShowAssignModal(false);
            showToast('Vehicle assigned successfully');
            fetchAssignments();
          }}
        />
      )}

      {/* Edit Modal */}
      {editAssignment && (
        <EditAssignmentModal
          assignment={editAssignment}
          onClose={() => setEditAssignment(null)}
          onSaved={() => {
            setEditAssignment(null);
            showToast('Assignment updated');
            fetchAssignments();
          }}
        />
      )}

      {/* Remove Confirm */}
      {removeConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="bg-surface rounded-2xl border border-border w-full max-w-sm shadow-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <Icon name="Unlink" size={20} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <h3 className="text-text-primary font-bold text-base">Remove Assignment</h3>
                <p className="text-text-secondary text-xs mt-0.5">
                  {removeConfirm?.vehicles?.year} {removeConfirm?.vehicles?.make} {removeConfirm?.vehicles?.model}
                </p>
              </div>
            </div>
            <p className="text-text-secondary text-sm">
              This will unlink the vehicle from <strong className="text-text-primary">{accountName}</strong>. The vehicle will become available for other assignments.
            </p>
            <div className="flex items-center justify-end gap-3 pt-1">
              <button onClick={() => setRemoveConfirm(null)} className="btn-secondary text-sm px-4 py-2">Cancel</button>
              <button
                onClick={() => handleRemove(removeConfirm?.id)}
                disabled={removing === removeConfirm?.id}
                className="text-sm px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}
              >
                {removing === removeConfirm?.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleAssignmentPanel;
